import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Check, QrCode } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { inventoryService } from '@/services/inventory';
import { barcodesService } from '@/services/barcodes';
import { env as appEnv } from '@/lib/env';
import type { GenerateBarcodesResult } from '@/types/barcodes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COUNT_PRESETS = [10, 25, 50, 100, 250];

/**
 * Generate a sheet of barcodes ahead of receiving stock or labelling units.
 * Codes are minted by the BE in `zfb_<env>_<12 hex>` format. Optionally
 * pre-bind to a SKU so the validate-scan flow can flag wrong-item scans
 * during inward.
 *
 * Two-state pattern (mirrors ApiKeyCreateSheet): form → "issued" panel
 * showing generated codes with copy-all + per-row copy.
 */
export function GenerateBarcodesSheet({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [count, setCount] = useState<number>(25);
  const [itemId, setItemId] = useState<string>('__none');
  const [issued, setIssued] = useState<GenerateBarcodesResult | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forSelect: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (open) {
      setCount(25);
      setItemId('__none');
      setIssued(null);
      setCopiedAll(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      barcodesService.generate({
        count,
        itemId: itemId !== '__none' ? itemId : undefined,
        // FE env mirrors deployment env so test/prod codes are visually distinct.
        env: appEnv.isProd ? 'live' : 'test',
      }),
    onSuccess: (res) => {
      setIssued(res);
      qc.invalidateQueries({ queryKey: ['barcodes'] });
      toast.success(`Generated ${res.count} ${res.count === 1 ? 'barcode' : 'barcodes'}`);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (count <= 0 || count > 500) return;
    mutation.mutate();
  };

  const copyAll = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.barcodes.map((b) => b.code).join('\n'));
      setCopiedAll(true);
      toast.success('All codes copied');
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      toast.error('Could not copy. Select and copy manually.');
    }
  };

  const items = itemsQuery.data?.items ?? [];
  const selectedItem = itemId !== '__none' ? items.find((i) => i.id === itemId) : null;

  // Issued-state panel
  if (issued) {
    return (
      <FormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={`Generated ${issued.count} ${issued.count === 1 ? 'barcode' : 'barcodes'}`}
        description={
          selectedItem
            ? `Pre-bound to ${selectedItem.sku}. Print these on labels and use them at inward.`
            : 'Print these on labels. Bind to a SKU at inward time.'
        }
        onSubmit={(e) => e.preventDefault()}
        hideFooter
        size="2xl"
      >
        <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2.5">
          <div className="flex items-start gap-2 text-[12.5px] text-foreground">
            <QrCode className="mt-0.5 h-4 w-4 shrink-0 text-info" />
            <div>
              <span className="font-medium">Codes are persisted server-side.</span>{' '}
              <span className="text-muted-foreground">
                You can revisit them from the Barcodes list. Copy now if you need them in your label
                designer.
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="text-[11.5px] text-muted-foreground">
            {issued.barcodes.length} codes minted
          </div>
          <Button type="button" size="sm" onClick={copyAll}>
            {copiedAll ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copiedAll ? 'Copied' : 'Copy all'}
          </Button>
        </div>

        <ScrollArea className="max-h-[40vh] rounded-md border border-border/70 bg-card">
          <ol className="divide-y divide-border/60 font-mono text-[12px]">
            {issued.barcodes.map((b, i) => (
              <li key={b.code} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-6 text-right text-[10.5px] tabular-nums text-muted-foreground/70">
                    {i + 1}
                  </span>
                  <span>{b.code}</span>
                </div>
                <button
                  type="button"
                  className="text-muted-foreground/60 transition-colors hover:text-foreground"
                  onClick={async () => {
                    await navigator.clipboard.writeText(b.code);
                    toast.success(`Copied ${b.code}`);
                  }}
                  aria-label={`Copy ${b.code}`}
                >
                  <Copy className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ol>
        </ScrollArea>

        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </FormSheet>
    );
  }

  // Form state
  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Generate barcodes"
      description="Mint a batch of unique codes ahead of printing labels. Codes can sit unconsumed in stock until used at inward."
      submitLabel={`Generate ${count} ${count === 1 ? 'code' : 'codes'}`}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="xl"
    >
      <FormField label="How many?" htmlFor="count" required>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {COUNT_PRESETS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCount(n)}
                className={
                  count === n
                    ? 'rounded-md border border-primary bg-primary/10 px-3 py-1 text-[12.5px] font-medium text-primary'
                    : 'rounded-md border border-border bg-background px-3 py-1 text-[12.5px] text-muted-foreground transition-colors hover:bg-muted/50'
                }
              >
                {n}
              </button>
            ))}
          </div>
          <Input
            id="count"
            type="number"
            min={1}
            max={500}
            value={count}
            onChange={(e) => setCount(Number(e.target.value) || 0)}
            className="font-mono tabular-nums"
          />
        </div>
      </FormField>

      <FormField
        label="Pre-bind to SKU (optional)"
        htmlFor="item"
        hint="When set, the validate-scan flow flags wrong-item scans during inward."
      >
        <Select value={itemId} onValueChange={setItemId}>
          <SelectTrigger id="item">
            <SelectValue placeholder={itemsQuery.isLoading ? 'Loading SKUs…' : 'Not pre-bound'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Not pre-bound</SelectItem>
            {items.map((it) => (
              <SelectItem key={it.id} value={it.id}>
                <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                <span className="ml-2">{it.name}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="rounded-md bg-muted/40 p-3 text-[11.5px] text-muted-foreground">
        Codes will be minted as <span className="font-mono">zfb_{appEnv.isProd ? 'live' : 'test'}_&lt;12 hex&gt;</span>.
        {' '}Each is unique within your tenant.
      </div>
    </FormSheet>
  );
}
